import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { catchError, map, of, ReplaySubject, startWith, switchMap, takeUntil } from 'rxjs'
import { csvRows } from './csv'
import '../typography/typography'

/** Noninteractive, literal first records of a CSV; the containing file card owns activation. */
@schmancyElement('schmancy-csv-thumbnail')
export class SchmancyCsvThumbnail extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				min-width: 0;
				height: 100%;
				overflow: hidden;
				pointer-events: none;
			}
		`,
	]
	@property() src = ''
	@property() label = ''
	@property({ attribute: 'failed-label' }) failedLabel?: string
	@state() private result: { status: 'loading' | 'ready' | 'error'; rows: string[][] } = { status: 'loading', rows: [] }
	private sources = new ReplaySubject<string>(1)

	override connectedCallback() {
		super.connectedCallback()
		this.sources
			.pipe(
				switchMap(src =>
					src
						? csvRows(src).pipe(
								map(rows => ({ status: 'ready' as const, rows })),
								catchError(error => {
									this.dispatchEvent(new CustomEvent('load-failed', { detail: error, bubbles: true, composed: true }))
									return of({ status: 'error' as const, rows: [] })
								}),
								startWith({ status: 'loading' as const, rows: [] }),
							)
						: of({ status: 'loading' as const, rows: [] }),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe(result => {
				this.result = result
				if (result.status === 'ready') this.dispatchEvent(new Event('load', { bubbles: true, composed: true }))
			})
	}

	override willUpdate(changed: PropertyValues<this>) {
		if (changed.has('src')) this.sources.next(this.src)
	}

	override render() {
		if (this.result.status !== 'ready' || !this.result.rows.length)
			return html` <div class="grid h-full place-items-center p-4" role="status">
				<schmancy-typography type="body" token="sm"
					>${this.result.status === 'loading'
						? componentText('csv.loading')
						: this.result.status === 'error'
							? (this.failedLabel ?? componentText('csv.failed'))
							: componentText('csv.empty')}</schmancy-typography
				>
			</div>`
		// A shared viewBox scales both axes together, including text and cell spacing.
		return html` <svg
			class="block h-full w-full"
			viewBox=${`0 0 400 ${Math.min(this.result.rows.length, 6) * 40}`}
			preserveAspectRatio="xMinYMin meet"
			role="img"
			aria-label=${this.label || componentText('csv.preview')}
		>
			<foreignObject width="400" height=${Math.min(this.result.rows.length, 6) * 40}>
				<table class="w-full table-fixed border-collapse" aria-hidden="true">
					<thead>
						<tr class="h-10">
							${this.result.rows[0].slice(0, 5).map(
								cell =>
									html`<th scope="col" class="bg-surface-container px-3 py-2 text-start">
										<schmancy-typography class="block max-w-full truncate" type="label" token="sm"
											>${cell}</schmancy-typography
										>
									</th>`,
							)}
						</tr>
					</thead>
					<tbody>
						${this.result.rows.slice(1, 6).map(
							row =>
								html`<tr class="h-10 even:bg-surface-containerLow">
									${row.slice(0, 5).map(
										cell =>
											html`<td class="px-3 py-2">
												<schmancy-typography class="block max-w-full truncate" type="body" token="sm"
													>${cell}</schmancy-typography
												>
											</td>`,
									)}
								</tr>`,
						)}
					</tbody>
				</table>
			</foreignObject>
		</svg>`
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'schmancy-csv-thumbnail': SchmancyCsvThumbnail
	}
}
